"use client";

import { useEffect, useSyncExternalStore } from "react";
import { TARIFA_TIPOS, type Tarifa, type TarifaTipo } from "./types";

/**
 * Catálogo de tarifas con descarga progresiva.
 *
 * La reserva vive debajo del pliegue, así que en vez de bloquear el render
 * pidiendo las 63 tarifas de golpe, arrancamos la descarga cuando el navegador
 * queda libre (`requestIdleCallback`) y traemos un tipo por hueco de reposo, en
 * el orden en que el usuario los necesita: primero los destinos, después las
 * zonas cortas y por último los traslados. Para cuando alguien baja al
 * formulario ya está todo en memoria.
 *
 * El estado vive fuera de React (módulo + `useSyncExternalStore`) para que la
 * descarga sobreviva a los desmontajes y no se dispare dos veces si hay más de
 * un componente suscrito.
 */

export interface EstadoTarifas {
  tarifas: Tarifa[];
  /** Tipos ya descargados: permite pintar el desplegable por partes. */
  listos: TarifaTipo[];
  cargando: boolean;
  completo: boolean;
  error: boolean;
}

const ESTADO_INICIAL: EstadoTarifas = {
  tarifas: [],
  listos: [],
  cargando: false,
  completo: false,
  error: false,
};

const CACHE_KEY = "taxi:tarifas:v1";
const CACHE_TTL_MS = 30 * 60 * 1000;

let estado: EstadoTarifas = ESTADO_INICIAL;
let arrancado = false;
/** Sube en cada `refrescarTarifas`: una descarga vieja que siga en vuelo se
 *  descarta en vez de mezclar sus tarifas con las de la nueva tanda. */
let generacion = 0;
const suscriptores = new Set<() => void>();

function actualizar(cambios: Partial<EstadoTarifas>) {
  estado = { ...estado, ...cambios };
  for (const avisar of suscriptores) avisar();
}

function suscribir(avisar: () => void) {
  suscriptores.add(avisar);
  return () => {
    suscriptores.delete(avisar);
  };
}

const leerEstado = () => estado;
const leerEstadoServidor = () => ESTADO_INICIAL;

/** Programa trabajo para cuando el hilo principal esté libre. El `timeout`
 *  evita que una pestaña muy ocupada deje la descarga esperando para siempre. */
function enReposo(tarea: () => void) {
  if (typeof window === "undefined") return;
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(tarea, { timeout: 2000 });
  } else {
    window.setTimeout(tarea, 200);
  }
}

function leerCache(): Tarifa[] | null {
  try {
    const crudo = sessionStorage.getItem(CACHE_KEY);
    if (!crudo) return null;
    const { ts, tarifas } = JSON.parse(crudo) as { ts: number; tarifas: Tarifa[] };
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return tarifas;
  } catch {
    return null;
  }
}

function guardarCache(tarifas: Tarifa[]) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ ts: Date.now(), tarifas }),
    );
  } catch {
    // sessionStorage lleno o bloqueado: la caché es un extra, no pasa nada.
  }
}

async function descargarTipo(tipo: TarifaTipo): Promise<Tarifa[]> {
  const opciones: RequestInit & { priority?: "high" | "low" | "auto" } = {
    // Prefetch de fondo: que no le quite ancho de banda a la página.
    priority: "low",
    headers: { Accept: "application/json" },
  };
  const res = await fetch(`/api/taxi/tarifas?tipo=${tipo}`, opciones);
  if (!res.ok) throw new Error(`Error ${res.status} al cargar ${tipo}`);
  return (await res.json()) as Tarifa[];
}

function descargarPorPartes(indice: number, mia: number, reintentos = 1) {
  if (mia !== generacion) return;

  const tipo = TARIFA_TIPOS[indice];
  if (!tipo) {
    guardarCache(estado.tarifas);
    actualizar({ cargando: false, completo: true });
    return;
  }

  enReposo(async () => {
    if (mia !== generacion) return;
    try {
      const nuevas = await descargarTipo(tipo);
      if (mia !== generacion) return;
      actualizar({
        tarifas: [...estado.tarifas, ...nuevas],
        listos: [...estado.listos, tipo],
        error: false,
      });
      descargarPorPartes(indice + 1, mia);
    } catch (error) {
      if (mia !== generacion) return;
      if (reintentos > 0) {
        window.setTimeout(
          () => descargarPorPartes(indice, mia, reintentos - 1),
          1500,
        );
        return;
      }
      console.error("[tarifas]", error);
      // Nos quedamos con lo que sí llegó: si ya hay destinos, la página sirve.
      actualizar({ cargando: false, error: true });
    }
  });
}

/** Arranca la descarga una sola vez por sesión de página. */
export function prefetchTarifas() {
  if (arrancado) return;
  arrancado = true;

  const mia = generacion;
  enReposo(() => {
    if (mia !== generacion) return;
    const enCache = leerCache();
    if (enCache?.length) {
      actualizar({
        tarifas: enCache,
        listos: [...TARIFA_TIPOS],
        cargando: false,
        completo: true,
      });
      return;
    }
    actualizar({ cargando: true, error: false });
    descargarPorPartes(0, mia);
  });
}

/** Vuelve a empezar desde cero (botón de reintentar). */
export function refrescarTarifas() {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // ignorar
  }
  generacion += 1;
  arrancado = false;
  actualizar(ESTADO_INICIAL);
  prefetchTarifas();
}

export function useTarifas(activo = true): EstadoTarifas {
  const actual = useSyncExternalStore(
    suscribir,
    leerEstado,
    leerEstadoServidor,
  );
  // `activo` en false evita bajar el catálogo cuando la pantalla que lo usa
  // está apagada: sin esto la página de taxi pedía las tarifas en cada visita
  // aunque el formulario no se enseñe.
  useEffect(() => {
    if (activo) prefetchTarifas();
  }, [activo]);
  return actual;
}
